import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/lib/apiBase';

const AuthContext = createContext(null);

const API = API_BASE_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const loginWithPin = async (email, pin) => {
    const response = await axios.post(`${API}/auth/login-pin`, { email, pin });
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    return userData;
  };


  const register = async (email, password, name, role = 'user') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Role helpers accept both new + legacy identifiers so tokens issued
  // before the role rename keep working until the user logs in again.
  const AGENCY_ADMIN_ROLES = ['agency_admin', 'super_admin', 'admin'];
  const ADMIN_ROLES = ['agency_admin', 'account_admin', 'super_admin', 'admin'];
  const STAFF_ROLES = [
    'agency_admin', 'agency_support', 'account_admin',
    'super_admin', 'admin', 'support_agent',
  ];

  const isAdmin = () => ADMIN_ROLES.includes(user?.role);
  const isStaff = () => STAFF_ROLES.includes(user?.role);
  const isSuperAdmin = () => AGENCY_ADMIN_ROLES.includes(user?.role);
  const isAgencyAdmin = () => AGENCY_ADMIN_ROLES.includes(user?.role);
  const isAccountAdmin = () => user?.role === 'account_admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithPin, register, logout, isAdmin, isStaff, isSuperAdmin, isAgencyAdmin, isAccountAdmin, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
